import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { SavedView, SavedViewPage } from "@api/graphql/generated/generated-types";
import { Loading } from "@src/common/components";
import { useRestoringViewContext } from "@src/shipments/context/RestoringViewContext";
import { useSavedViewsQuery, useSavedViewsMutations } from "../hooks";
import { SavedViewMapping } from "../types";

interface SavedViewsContextProps {
  savedView: SavedView | null;
  savedViews: SavedView[];
  savedViewsLoading: boolean;
  isApplied: boolean;
  applySavedView: () => void;
  switchToView: (viewId: string) => void;
  clearAppliedView: () => void;
  saveCurrentView: (name: string) => Promise<void>;
  updateSavedView: (name: string) => Promise<void>;
  deleteSavedView: (viewId: string) => Promise<void>;
  refetchSavedViews: () => void;
}

const SavedViewsContext =
  createContext<SavedViewsContextProps | undefined>(undefined);

interface Props {
  children: React.ReactNode;
  // Which page's views to load and save.
  page: SavedViewPage;
  // The page's adapter hook (see SavedViewMapping). Called unconditionally on
  // every render, so pass a stable hook, not an inline closure.
  useViewMapping: () => SavedViewMapping;
}

export const SavedViewsProvider = ({ children, page, useViewMapping }: Props) => {
  const [isApplied, setIsApplied] = useState(false);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const hasAutoApplied = useRef(false);
  const { withRestoring } = useRestoringViewContext();

  // Query hook
  const { savedViews, setSavedViews, loading, refetchSavedViews } =
    useSavedViewsQuery(page);

  // Derive active view from array + activeViewId
  const savedView = savedViews.find((v) => v.id === activeViewId) || null;

  // Page adapter: filters / sort in and out of the page's own contexts
  const {
    selectedPrimaryFilter,
    buildFiltersFromContext,
    buildSortFieldsFromContext,
    applyViewToFilters,
    clearFiltersToDefault,
  } = useViewMapping();

  // Mutations hook
  const { createSavedView, updateSavedView, deleteSavedView, setDefaultSavedView } =
    useSavedViewsMutations({
      page,
      onCreateSuccess: (view) => {
        setSavedViews((prev) => [...prev, view]);
        setActiveViewId(view.id);
        setIsApplied(true);
        setDefaultSavedView(view.id);
      },
      onUpdateSuccess: (view) => {
        setSavedViews((prev) =>
          prev.map((v) => (v.id === view.id ? view : v))
        );
      },
      onDeleteSuccess: (deletedViewId: string) => {
        setSavedViews((prev) => prev.filter((v) => v.id !== deletedViewId));
        if (deletedViewId === activeViewId) {
          setActiveViewId(null);
          setIsApplied(false);
          clearFiltersToDefault();
        }
      },
    });

  // Auto-apply saved view on initial load
  useEffect(() => {
    if (!loading && savedViews.length > 0 && !hasAutoApplied.current) {
      // Use the default view, or fall back to the first view
      const defaultView = savedViews.find((v) => v.isDefault) || savedViews[0];
      setActiveViewId(defaultView.id);
      withRestoring(() => applyViewToFilters(defaultView));
      setIsApplied(true);
      hasAutoApplied.current = true;
    }
  }, [loading, savedViews]);

  // Actions
  const applySavedView = () => {
    if (!savedView) return;
    withRestoring(() => applyViewToFilters(savedView));
    setIsApplied(true);
  };

  const switchToView = (viewId: string) => {
    const view = savedViews.find((v) => v.id === viewId);
    if (!view) return;
    setActiveViewId(viewId);
    withRestoring(() => applyViewToFilters(view));
    setIsApplied(true);
    setDefaultSavedView(viewId);
  };

  const clearAppliedView = () => {
    clearFiltersToDefault();
    setActiveViewId(null);
    setIsApplied(false);
  };

  const handleSaveCurrentView = async (name: string): Promise<void> => {
    const filters = buildFiltersFromContext();
    const sortFields = buildSortFieldsFromContext();
    return createSavedView(name, selectedPrimaryFilter, filters, sortFields);
  };

  const handleUpdateSavedView = async (name: string): Promise<void> => {
    if (!savedView) return;
    const filters = buildFiltersFromContext();
    const sortFields = buildSortFieldsFromContext();
    return updateSavedView(
      savedView.id,
      name,
      selectedPrimaryFilter,
      filters,
      sortFields
    );
  };

  const handleDeleteSavedView = async (viewId: string): Promise<void> => {
    return deleteSavedView(viewId);
  };

  // Block rendering children until saved views are loaded
  if (loading) {
    return (
      <Loading loading={true} text="Loading saved view...">
        <div className="h-60" />
      </Loading>
    );
  }

  return (
    <SavedViewsContext.Provider
      value={{
        savedView,
        savedViews,
        savedViewsLoading: loading,
        isApplied,
        applySavedView,
        switchToView,
        clearAppliedView,
        saveCurrentView: handleSaveCurrentView,
        updateSavedView: handleUpdateSavedView,
        deleteSavedView: handleDeleteSavedView,
        refetchSavedViews,
      }}
    >
      {children}
    </SavedViewsContext.Provider>
  );
};

export const useSavedViewsContext = () => {
  const context = useContext(SavedViewsContext);
  if (context === undefined) {
    throw new Error(
      "useSavedViewsContext must be used within a SavedViewsProvider"
    );
  }
  return context;
};
